import axiosInstance from './axiosInstance'
import { TagMini, AddActivityPayload } from './leadsApi'

// ── Types ─────────────────────────────────────────────────────────────────────

export const CUSTOMER_SOURCES = [
  'FACEBOOK', 'INSTAGRAM', 'WHATSAPP', 'WEBSITE', 'REFERRAL', 'WALK_IN', 'PHONE_CALL', 'OTHER',
] as const
export type CustomerSource = typeof CUSTOMER_SOURCES[number]

export interface AuditUser {
  id: string
  name: string
  email: string
  avatarUrl: string | null
}

export interface ActivityLog {
  id: string
  entityType: string
  entityId: string
  entityName: string
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  changes: Record<string, unknown> | null
  performedBy: AuditUser
  createdAt: string
}

export interface Contact {
  id: string
  name: string
  phone: string
  email: string | null
  role: string | null
  isPrimary: boolean
  customerId: string
  customer: { id: string; name: string; phone: string; companyName: string | null }
  createdBy: AuditUser | null
  createdAt: string
  updatedAt: string
}

export interface ConvertedFromLead {
  id: string
  name: string
  phone: string
  email: string | null
  source: string
  status: string
  createdAt: string
  convertedAt: string | null
  owner: { id: string; name: string }
  assignedTo: { id: string; name: string } | null
  activityCount: number
}

export interface Customer {
  id: string
  name: string
  phone: string
  email: string | null
  companyName: string | null
  city: string | null
  description: string | null
  source: string
  count: { contacts: number }
  createdBy: AuditUser | null
  assignedTo: AuditUser | null
  tags: TagMini[]
  createdAt: string
  updatedAt: string
  convertedFromLead: ConvertedFromLead | null
  // ── Tenant context (read-only) ───────────────────────────────────────────
  orgId: string | null
  orgName: string | null
  lobId: string | null
  lobName: string | null
  warehouseId: string | null
  warehouseName: string | null
  address: string | null
  gst: string | null
  pincode: string | null
  designation: string | null
  websiteUrl: string | null
  industryType: string | null
}

export interface PaginatedCustomers {
  items: Customer[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface CreateCustomerPayload {
  name: string
  phone: string
  email?: string
  companyName?: string
  city?: string
  source?: CustomerSource
  // SuperAdmin tenant targeting (org → lob → warehouse)
  orgId?: string
  lobId?: string
  warehouseId?: string
  address?: string
  gst?: string
  pincode?: string
}

export interface UpdateCustomerPayload {
  name?: string
  phone?: string
  email?: string | null
  companyName?: string | null
  city?: string | null
  description?: string | null
  source?: CustomerSource
  assignedToId?: string | null
  tagIds?: string[]
  address?: string | null
  gst?: string | null
  pincode?: string | null
}

export interface CustomerFilters {
  search?: string
  sources?: string[]
  city?: string
  // SuperAdmin cross-tenant filter
  orgIds?: string[]
  lobIds?: string[]
  warehouseIds?: string[]
  fromDate?: string
  toDate?: string
  ownerId?: string
  customerTypeIds?: number[]
  page?: number
  pageSize?: number        // matches backend ?pageSize= parameter
  companyNameSearch?: string  // company-name-only search for autocomplete
}

export interface CreateContactPayload {
  name: string
  phone: string
  email?: string
  role?: string
}

export interface UpdateContactPayload {
  name?: string
  phone?: string
  email?: string | null
  role?: string | null
}

export interface ContactFilters {
  search?: string
  customerId?: string
  page?: number
  pageSize?: number  // matches backend ?pageSize= parameter
}

export interface PaginatedContacts {
  items: Contact[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// ── Contact duplicate check ───────────────────────────────────────────────────

export interface ContactDuplicateResult {
  phoneExists:   boolean
  emailExists:   boolean
  phoneMessage:  string | null
  emailMessage:  string | null
}

// ── Duplicate detection types ─────────────────────────────────────────────────

export type DuplicateMatchReason = 'ExactName' | 'ExactPhone' | 'ExactEmail' | 'FuzzyName'

export interface DuplicateMatch {
  id: string
  name: string
  companyName: string | null
  phone: string
  email: string | null
  city: string | null
  matchReason: DuplicateMatchReason
  similarity: number | null
}

export interface DuplicateCheckResult {
  hasExactMatch: boolean
  exactMatches: DuplicateMatch[]
  hasFuzzyMatches: boolean
  fuzzyMatches: DuplicateMatch[]
}

// ── Customer API ──────────────────────────────────────────────────────────────

export const customersApi = {
  export: async (filters: CustomerFilters = {}): Promise<void> => {
    const res = await axiosInstance.get('/customers/export', { params: filters, responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Companies.xlsx')
    document.body.appendChild(link)
    link.click()
    link.remove()
  },

  getAll: async (filters: CustomerFilters = {}): Promise<PaginatedCustomers> => {
    const res = await axiosInstance.get<{ data: PaginatedCustomers }>('/customers', { params: filters })
    return res.data.data
  },

  getById: async (id: string): Promise<Customer> => {
    const res = await axiosInstance.get<{ data: Customer }>(`/customers/${id}`)
    return res.data.data
  },

  create: async (payload: CreateCustomerPayload): Promise<Customer> => {
    const res = await axiosInstance.post<{ data: Customer }>('/customers', payload, { _suppressToast: true } as any)
    return res.data.data
  },

  checkDuplicate: async (params: {
    name?: string
    phone?: string
    email?: string
    excludeId?: string
  }): Promise<DuplicateCheckResult> => {
    const res = await axiosInstance.get<{ data: DuplicateCheckResult }>(
      '/customers/check-duplicate',
      { params, _suppressToast: true } as any
    )
    return res.data.data
  },

  checkContactDuplicate: async (params: {
    phone?: string
    email?: string
    excludeId?: string
  }): Promise<ContactDuplicateResult> => {
    const res = await axiosInstance.get<{ data: ContactDuplicateResult }>(
      '/contacts/check-duplicate',
      { params, _suppressToast: true } as any
    )
    return res.data.data
  },

  update: async (id: string, payload: UpdateCustomerPayload): Promise<Customer> => {
    const res = await axiosInstance.put<{ data: Customer }>(`/customers/${id}`, payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/customers/${id}`)
  },

  getContacts: async (customerId: string): Promise<Contact[]> => {
    const res = await axiosInstance.get<{ data: Contact[] }>(`/customers/${customerId}/contacts`)
    return res.data.data
  },

  addContact: async (customerId: string, payload: CreateContactPayload): Promise<Contact> => {
    const res = await axiosInstance.post<{ data: Contact }>(`/customers/${customerId}/contacts`, payload)
    return res.data.data
  },

  getActivity: async (customerId: string): Promise<ActivityLog[]> => {
    const res = await axiosInstance.get<{ data: ActivityLog[] }>(`/customers/${customerId}/activity`)
    return res.data.data
  },

  assign: async (id: string, assignedToId: string): Promise<Customer> => {
    const res = await axiosInstance.patch<{ data: Customer }>(`/customers/${id}/assign`, { assignedToId })
    return res.data.data
  },

  addTag: async (id: string, tagId: string): Promise<Customer> => {
    const res = await axiosInstance.post<{ data: Customer }>(`/customers/${id}/tags/${tagId}`)
    return res.data.data
  },

  removeTag: async (id: string, tagId: string): Promise<Customer> => {
    const res = await axiosInstance.delete<{ data: Customer }>(`/customers/${id}/tags/${tagId}`)
    return res.data.data
  },

  addActivity: async (id: string, payload: AddActivityPayload): Promise<any> => {
    const res = await axiosInstance.post<{ data: any }>(`/customers/${id}/activities`, payload)
    return res.data.data
  },
}
